import { Outlet } from 'react-router-dom'
import {
  Activity,
  Building2,
  CreditCard,
  HardDrive,
  History,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Settings,
} from 'lucide-react'

import { t } from '../../lib/i18n'
import { routes } from '../../lib/routes'
import { SidebarNav, type NavSection } from '../../components'
import { useSession, useSessionState } from '../../app/session'
import { useBusinesses, useTickets } from '../../fixtures'

/**
 * The Platform Console shell. Accent INDIGO.
 *
 * The support account's navigation is BUILT SHORTER, not disabled: Plans and
 * Console settings' owner-only half simply do not appear. A greyed-out row
 * still announces what exists and invites a request for it.
 */
export function PlatformLayout() {
  const { platformRole, platformUserName } = useSession()
  const { signOut } = useSessionState()
  const businesses = useBusinesses()
  const tickets = useTickets()

  const isOwner = platformRole === 'PLATFORM_OWNER'
  const openTickets = tickets.data?.filter((ticket) => ticket.status === 'OPEN').length

  const sections: NavSection[] = [
    {
      id: 'platform',
      title: t('p.nav.platform'),
      items: [
        {
          to: routes.platform.overview(),
          label: t('p.nav.overview'),
          icon: <LayoutDashboard className="size-full" />,
          end: true,
        },
        {
          to: routes.platform.businesses(),
          label: t('p.nav.businesses'),
          icon: <Building2 className="size-full" />,
          // The deck's number was 47. It is now however many there are.
          count: businesses.data?.length,
        },
        {
          to: routes.platform.subscriptions(),
          label: t('p.nav.subscriptions'),
          icon: <CreditCard className="size-full" />,
          attention: true,
        },
        // Plans are the owner's to price. Absent, not disabled.
        ...(isOwner
          ? [
              {
                to: routes.platform.plans(),
                label: t('p.nav.plans'),
                icon: <Layers className="size-full" />,
              },
            ]
          : []),
      ],
    },
    {
      id: 'operations',
      title: t('p.nav.operations'),
      items: [
        {
          to: routes.platform.usage(),
          label: t('p.nav.usage'),
          icon: <HardDrive className="size-full" />,
        },
        {
          to: routes.platform.health(),
          label: t('p.nav.health'),
          icon: <Activity className="size-full" />,
        },
        {
          to: routes.platform.support(),
          label: t('p.nav.support'),
          icon: <LifeBuoy className="size-full" />,
          count: openTickets,
        },
        {
          to: routes.platform.accessLog(),
          label: t('p.nav.accessLog'),
          icon: <History className="size-full" />,
        },
      ],
    },
    {
      id: 'you',
      title: t('p.nav.you'),
      items: [
        {
          to: routes.platform.settings(),
          label: t('p.nav.settings'),
          icon: <Settings className="size-full" />,
        },
      ],
    },
  ]

  return (
    <div className="flex h-dvh overflow-hidden" data-console="platform">
      <SidebarNav
        brand={t('p.brand')}
        brandSub={t('p.brandSub')}
        sections={sections}
        user={{
          name: platformUserName,
          role: t(`platformRole.${platformRole}`),
        }}
        onSignOut={() => void signOut()}
      />
      <main className="no-scrollbar flex-1 overflow-y-auto px-7 pt-6 pb-12">
        <Outlet />
      </main>
    </div>
  )
}
